import React, { useEffect, useState } from 'react';
import ConfirmationModal from "../components/ConfirmationModal";
import { TableActionColumnHeader } from "../components/TableShell";
import ShieldCareSectionTabs, { SHIELD_CARE_TAB_INTAKE_LOGS } from "../components/ShieldCareSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import { IconEdit, IconDelete, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const ErrorBanner = require('../components/ErrorBanner').default;

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

function ShieldCareIntakeLogs() {
  const [currentObjs, setCurrentObjs] = useState([]);
  const [medications, setMedications] = useState([]);
  const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
  const account_id = userInfo.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [_id, setID] = useState(null);
  const [patientId, setPatientId] = useState('');
  const [medicationId, setMedicationId] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [actualTakenTime, setActualTakenTime] = useState('');
  const [status, setStatus] = useState('pending');
  const [notes, setNotes] = useState('');
  const [edit, setEdit] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [formExpanded, setFormExpanded] = useState(false);
  const [bannerError, setBannerError] = useState('');

  // Search filters
  const [searchPatientId, setSearchPatientId] = useState('');
  const [searchMedicationId, setSearchMedicationId] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchNotes, setSearchNotes] = useState('');
  const [searchScheduledFrom, setSearchScheduledFrom] = useState('');
  const [searchScheduledTo, setSearchScheduledTo] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm", // "confirm" | "alert"
    onConfirm: null,
  });

  useEffect(() => {
    getDocument();
    getMedications();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch
  }, []);

  function toISOInput(value) {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 16);
  }

  function getDocument(searchOverride) {
    if (!account_id) return;
    const pId = searchOverride && 'patientId' in searchOverride ? searchOverride.patientId : searchPatientId;
    const mId = searchOverride && 'medicationId' in searchOverride ? searchOverride.medicationId : searchMedicationId;
    const st = searchOverride && 'status' in searchOverride ? searchOverride.status : searchStatus;
    const sn = searchOverride && 'searchNotes' in searchOverride ? searchOverride.searchNotes : searchNotes;
    const from = searchOverride && 'scheduledFrom' in searchOverride ? searchOverride.scheduledFrom : searchScheduledFrom;
    const to = searchOverride && 'scheduledTo' in searchOverride ? searchOverride.scheduledTo : searchScheduledTo;
    const body = {
      account_id,
      ...(pId && String(pId).trim() && { patientId: String(pId).trim() }),
      ...(mId && String(mId).trim() && { medicationId: String(mId).trim() }),
      ...(st && String(st).trim() && { status: String(st).trim() }),
      ...(sn && String(sn).trim() && { searchNotes: String(sn).trim() }),
      ...(from && { scheduledFrom: new Date(from).toISOString() }),
      ...(to && { scheduledTo: new Date(to).toISOString() }),
    };
    apiFetch(serverUrl + '/getIntakeLogs', {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then((resp) => resp.json())
      .then((data) => setCurrentObjs(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function getMedications() {
    if (!account_id) return;
    apiFetch(serverUrl + '/getMedications', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setMedications(Array.isArray(data) ? data : []))
      .catch(() => {});
  }

  function createDocument() {
    apiFetch(serverUrl + '/createIntakeLog', {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        patientId: patientId.trim(),
        medicationId: medicationId.trim(),
        scheduledTime: scheduledTime ? new Date(scheduledTime).toISOString() : undefined,
        actualTakenTime: actualTakenTime ? new Date(actualTakenTime).toISOString() : undefined,
        status: status.trim() || 'pending',
        notes: notes.trim(),
      }),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Intake log", message: data, mode: "alert", onConfirm: null });
        getDocument();
        collapseForm();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function updateDocument() {
    apiFetch(serverUrl + '/updateIntakeLog', {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        _id,
        patientId: patientId.trim(),
        medicationId: medicationId.trim(),
        scheduledTime: scheduledTime ? new Date(scheduledTime).toISOString() : undefined,
        actualTakenTime: actualTakenTime ? new Date(actualTakenTime).toISOString() : undefined,
        status: status.trim() || 'pending',
        notes: notes.trim(),
      }),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Intake log", message: data, mode: "alert", onConfirm: null });
        getDocument();
        collapseForm();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function deleteDocument(currentObj) {
    const { _id: id } = currentObj;
    apiFetch(serverUrl + '/deleteIntakeLog', {
      method: 'POST',
      body: JSON.stringify({ account_id, _id: id }),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Intake log", message: data, mode: "alert", onConfirm: null });
        const newSelectedIds = new Set(selectedIds);
        newSelectedIds.delete(id);
        setSelectedIds(newSelectedIds);
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  const createUpdate = (e) => {
    e.preventDefault();
    const pTrim = (patientId != null ? String(patientId) : '').trim();
    const mTrim = (medicationId != null ? String(medicationId) : '').trim();
    if (!pTrim) {
      setConfirmDialog({ open: true, title: "Intake log", message: "Patient ID is required.", mode: "alert", onConfirm: null });
      return;
    }
    if (!mTrim) {
      setConfirmDialog({ open: true, title: "Intake log", message: "Medication ID is required.", mode: "alert", onConfirm: null });
      return;
    }
    if (edit) updateDocument();
    else createDocument();
  };

  const collapseForm = () => {
    setFormExpanded(false);
    setID(null);
    setPatientId('');
    setMedicationId('');
    setScheduledTime('');
    setActualTakenTime('');
    setStatus('pending');
    setNotes('');
    setEdit(false);
  };

  const fillForm = (obj) => {
    setID(obj._id);
    setPatientId(obj.patientId || '');
    setMedicationId(obj.medicationId || '');
    setScheduledTime(toISOInput(obj.scheduledTime));
    setActualTakenTime(toISOInput(obj.actualTakenTime));
    setStatus(obj.status || 'pending');
    setNotes(obj.notes || '');
    setEdit(true);
    setFormExpanded(true);
  };

  function handleSort(key) {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  }

  function getFilteredAndSortedData() {
    let filtered = currentObjs || [];
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        const aValue = aVal instanceof Date ? aVal.getTime() : (aVal ?? '');
        const bValue = bVal instanceof Date ? bVal.getTime() : (bVal ?? '');
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  function handleSelectAll(e) {
    const filteredData = getFilteredAndSortedData();
    const newSelectedIds = new Set(selectedIds);
    if (e.target.checked) filteredData.forEach((obj) => newSelectedIds.add(obj._id));
    else filteredData.forEach((obj) => newSelectedIds.delete(obj._id));
    setSelectedIds(newSelectedIds);
  }

  function handleSelectOne(id, checked) {
    const newSelectedIds = new Set(selectedIds);
    if (checked) newSelectedIds.add(id);
    else newSelectedIds.delete(id);
    setSelectedIds(newSelectedIds);
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete intake logs", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete intake logs",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        let successCount = 0;
        let errorCount = 0;
        for (const id of idsToDelete) {
          try {
            const response = await apiFetch(serverUrl + '/deleteIntakeLog', {
              method: 'POST',
              body: JSON.stringify({ account_id, _id: id }),
            });
            if (response.ok) successCount++;
            else errorCount++;
          } catch {
            errorCount++;
          }
        }
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: `Delete completed! Success: ${successCount}, Errors: ${errorCount}`,
          onConfirm: () => {
            setSelectedIds(new Set());
            getDocument();
            setDeleting(false);
          },
        });
      },
    });
  }

  const allRequiredFilled = Boolean(String(patientId ?? '').trim() && String(medicationId ?? '').trim());

  const filteredData = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredData);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);
  const STATUS_OPTIONS = ['pending', 'taken', 'missed', 'skipped', 'delayed'];

  return (
    <div className="page-container page-container-full page-container--section-tabs">
      <SectionTabsPageHeader subtitle="Track medication intake logs.">
        <ShieldCareSectionTabs activeTab={SHIELD_CARE_TAB_INTAKE_LOGS} />
      </SectionTabsPageHeader>
      <div className="page-content">
        <div className="page-top">
          <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />
          <ConfirmationModal
            open={confirmDialog.open}
            title={confirmDialog.title || "Confirm"}
            message={confirmDialog.message}
        mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
        confirmLabel={confirmDialog.mode === "alert" ? "OK" : undefined}
            onConfirm={() => {
              const fn = confirmDialog.onConfirm;
          setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
              if (fn) fn();
            }}
            onCancel={() =>
          setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null })
            }
          />
          {formExpanded && (
            <div className="panel">
              <div className="panel-title">
                {edit ? 'Edit intake log' : 'Add new intake log'}{' '}
                <span className="panel-required-hint">(Patient ID & Medication ID required)</span>
              </div>
              <div className="panel-row">
                <input type="text" value={patientId ?? ''} placeholder="Patient ID *" onChange={(e) => setPatientId(e.target.value)} className="input input-min-120" />
                <input type="text" value={medicationId} onChange={(e) => setMedicationId(e.target.value)} placeholder="Medication ID *" list="intake-log-medication-list" className="input input-min-180" />
                <datalist id="intake-log-medication-list">
                  {medications.map((m) => (
                    <option key={m._id} value={m._id} label={m.medicationName} />
                  ))}
                </datalist>
                <input type="datetime-local" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="input input-min-140" />
                <input type="datetime-local" value={actualTakenTime} onChange={(e) => setActualTakenTime(e.target.value)} className="input input-min-140" />
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="input select input-min-100">
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <input type="text" value={notes ?? ''} placeholder="Notes" onChange={(e) => setNotes(e.target.value)} className="input input-min-140" />
                <button type="button" onClick={(e) => createUpdate(e)} disabled={!allRequiredFilled} className="btn btn-primary">{edit ? 'Update' : 'Create'}</button>
                {edit && <button type="button" onClick={collapseForm} className="btn btn-secondary">Cancel</button>}
              </div>
            </div>
          )}
          <div className="toolbar">
            <input type="text" value={searchPatientId} onChange={(e) => setSearchPatientId(e.target.value)} placeholder="Patient ID" className="input input-min-100" />
            <input type="text" value={searchMedicationId} onChange={(e) => setSearchMedicationId(e.target.value)} placeholder="Medication ID" className="input input-min-120" />
            <select value={searchStatus} onChange={(e) => setSearchStatus(e.target.value)} className="input select input-min-100">
              <option value="">Status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <input type="text" value={searchNotes} onChange={(e) => setSearchNotes(e.target.value)} placeholder="Notes" className="input input-min-120" />
            <input type="datetime-local" value={searchScheduledFrom} onChange={(e) => setSearchScheduledFrom(e.target.value)} placeholder="From" className="input input-min-140" />
            <input type="datetime-local" value={searchScheduledTo} onChange={(e) => setSearchScheduledTo(e.target.value)} placeholder="To" className="input input-min-140" />
            <button type="button" onClick={getDocument} className="btn btn-primary">Search</button>
            <button type="button" onClick={() => { setSearchPatientId(''); setSearchMedicationId(''); setSearchStatus(''); setSearchNotes(''); setSearchScheduledFrom(''); setSearchScheduledTo(''); getDocument({ patientId: '', medicationId: '', status: '', searchNotes: '', scheduledFrom: '', scheduledTo: '' }); }} className="btn btn-secondary btn-sm">Clear</button>
            <span className="toolbar-divider" />
            <button type="button" onClick={() => { if (formExpanded) collapseForm(); else setFormExpanded(true); }} className={`btn btn-md ${formExpanded ? 'btn-primary-outline' : 'btn-primary'}`}>{formExpanded ? '− New' : '+ New'}</button>
            <button type="button" onClick={deleteSelected} disabled={selectedVisibleCount === 0 || deleting} className="btn btn-danger">Delete{selectedCountSuffix}</button>
          </div>
        </div>
        <div className="table-scroll">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" checked={filteredData.length > 0 && filteredData.every((obj) => selectedIds.has(obj._id))} onChange={handleSelectAll} disabled={filteredData.length === 0} />
                  </th>
                  <th className="sortable" onClick={() => handleSort('patientId')}>Patient ID</th>
                  <th className="sortable" onClick={() => handleSort('medicationId')}>Medication ID</th>
                  <th className="sortable" onClick={() => handleSort('scheduledTime')}>Scheduled</th>
                  <th className="sortable" onClick={() => handleSort('actualTakenTime')}>Actual Taken</th>
                  <th className="sortable" onClick={() => handleSort('status')}>Status</th>
                  <th>Notes</th>
                  <th>Created</th>
                  <TableActionColumnHeader align="left" />
                </tr>
              </thead>
              <tbody>
                {filteredData.map((currentObj) => (
                  <tr key={currentObj._id}>
                    <td>
                      <input type="checkbox" checked={selectedIds.has(currentObj._id)} onChange={(e) => handleSelectOne(currentObj._id, e.target.checked)} />
                    </td>
                    <td className="table-cell-ellipsis">{currentObj.patientId || '—'}</td>
                    <td>{currentObj.medicationId || '—'}</td>
                    <td>{formatDateTime(currentObj.scheduledTime)}</td>
                    <td>{formatDateTime(currentObj.actualTakenTime)}</td>
                    <td>{currentObj.status || '—'}</td>
                    <td className="table-cell-ellipsis-sm" title={currentObj.notes}>{currentObj.notes || '—'}</td>
                    <td>{formatDateTime(currentObj.createdAt)}</td>
                    <td>
                      <RowActionIcons>
                        <button type="button" onClick={() => fillForm(currentObj)} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                        <button type="button" onClick={() => deleteDocument(currentObj)} className="btn-link-danger" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                      </RowActionIcons>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShieldCareIntakeLogs;
